import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import ModelFactory from 'business/models/ModelFactory';
import {
  PFDocProteinFormulationIngredientsDTO,
  SeedDTO,
  SeedFormulationConrollerClient,
  SeedSolutionIngredientsDTO,
  SeedWellDTO,
  WellInfoClient,
  WellLayerIngredientDTO,
} from 'business/models/Models';
import RMTable from 'components/customComponents/Controls/RMTable';
import LabelTextBox from 'components/customComponents/LabelTextBox';
import { EISelectBox } from 'components/customComponents/Layers/EICheckBox';
import RecentImagedCarousel from 'components/UpdatedDashboard/CustomComponents/RecentImagedCarousel';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { useRMSelector } from 'redux/hooks';
import { showAlertSnackBar } from 'utils/Global';
import WellService from 'business/services/WellService';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import './SeedStyles.scss';
import { IStyle } from 'Styles';
import { getLatestImageBatchId } from 'components/plateviewer/experiment/Optimization/IterativeScreenOptimizationHelper';
import { getRegionId } from 'components/commandpanel/Navigator/DRCIPWUtil';
import { CustomCheckBox } from 'components/input';
import { WellIng, WellNavigationDetails, Drop } from 'components/Experiment/constants';
import {
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import { FmlxBackdropLoading } from 'fmlx-common-ui';
import { useFileNotFoundModal } from 'CustomHooks/useFileNotFoundModal';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';

export type SeedFormulationProps = {
  show?: boolean;
  setShow?: (boolean) => void;
  selectedWells?: WellNavigationDetails[];
  treeNodeId?: number;
  isNew?: boolean;
  setSeedName?: (seedName: string) => void;
  setIsSeedEdited?: (flag: boolean) => void;
  setAccessRightName?: (accessRightName: string) => void;
  accessRightName?: string;
};

const loadSeedDTO = async (treeNodeId) => {
  const seedformulationClient: SeedFormulationConrollerClient = ModelFactory(
    SeedFormulationConrollerClient,
    false,
    true,
  );
  const seedInfo: SeedDTO = await seedformulationClient.getSeedInfo(treeNodeId);
  return seedInfo;
};

const updateSeed = async (seedDto: SeedDTO, showFileNotFoundModal) => {
  const seedformulationClient: SeedFormulationConrollerClient = ModelFactory(
    SeedFormulationConrollerClient,
  );
  try {
    const saved = await seedformulationClient.updateSeed(seedDto);
    if (saved) {
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        'Seed Formulation saved successfully.',
      );
    } else {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'Could not save due to unknown error',
      );
    }
  } catch (error) {
    if (error.status === 409) {
      //conflict
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Entity with same name exists already',
      );
    } else if (error.StatusCode === 404) {
      showFileNotFoundModal();
    } else {
      throw error;
    }
  }
};

const getIngListDiv = (value: string | number) => (
  <div className="dataStyle">{value}</div>
);

const styles: IStyle = {
  inputStyle: {
    marginLeft: '28px',
    width: '182px',
    fontSize: '12px',
    height: '26px',
  },

  inputStylePlateId: {
    marginLeft: '37px',
    width: '50px',
    fontSize: '12px',
  },

  labelStylePlateId: {
    marginLeft: '12px',
    width: '60px',
  },

  labelStyle: {
    marginLeft: '12px',
    width: '69px',
  },

  inputStyleDrop: {
    marginLeft: '13px',
    width: '50px',
    fontSize: '12px',
  },

  labelStyleDrop: {
    marginLeft: '23px',
  },

  solutionInputStyle: {
    marginLeft: '0.5rem',
  },

  solutionBoxLabelStyle: {
    marginLeft: '12px',
    width: '69px',
  },

  solutionBoxSelectStyle: {
    marginLeft: '28px',
    width: '11rem',
    fontSize: '12px',
    height: '26px',
  },

  addButtonStyle: {
    width: '2rem',
    height: '2rem',
    marginLeft: '25px',
    marginRight: '14px',
  },

  removeButtonStyle: {
    width: '44px',
    height: '44px',
    marginLeft: '0.6rem',
    backgroundColor: '#666666',
    borderRadius: '4px',
  },

  saveButtonCustomStyle: {
    backgroundColor: '#F7A246',
    width: '9.5rem',
  },

  blankImageStyle: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bolder',
    color: 'grey',
  },
};

interface seedData {
  name: string;
  id: number;
  seedDate: Date;
  seedPlateId: number;
  seedDropNumber: number;
  pfName: string;
  seedWellNumbers: string;
  pfIng: any;
  notesText: string;
  solutionWellId: number;
  useWellSolChecked: boolean;
  treeNodeId: number;
}

const seedPfColumns = [
  {
    Header: 'Type',
    accessor: 'type',
  },
  {
    Header: 'Name',
    accessor: 'name',
  },
  {
    Header: 'Conc. (mg/mL)',
    accessor: 'concMg',
  },
  {
    Header: 'Conc. (mM)',
    accessor: 'concMm',
  },
];

const defaultSeedData = {
  name: '',
  id: 0,
  seedDate: undefined,
  seedPlateId: 0,
  seedDropNumber: 0,
  pfName: '',
  seedWellNumbers: '',
  pfIng: [],
  notesText: '',
  solutionWellId: 0,
  useWellSolChecked: false,
  treeNodeId: 0,
};

export const SeedFormulationModal = forwardRef((props: SeedFormulationProps, ref) => {
  const {
    setShow,
    selectedWells,
    treeNodeId,
    isNew,
    setSeedName,
    setIsSeedEdited,
    setAccessRightName,
    accessRightName,
  } = props;
  const isReadOnly = !isNew && accessRightName !== ETreeNodeAccessRightNames.Modify;
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { plateID } = wellNavigationDetails || {};
  const { cpId, dropNumber } = selectedDropImageParameters || {};
  const [conditionWellId, setConditionWellId] = useState(0);
  const [selectedWellItems, setSelectedWellItems] = useState([]);
  const [wellLayerIngList, setWellLayerIngList] = useState([]);
  const [solutionWellLayerIngList, setSolutionWellLayerIngList] = useState([]);
  const [wellSolutionEditData, setWellSolutionEditData] = useState([[]]);
  const [isDirty, setIsDirty] = useState(false);
  const [solutionWellLayerIngListText, setSolutionWellLayerIngListText] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [seedDtoData, setSeedDtoData] = useState<seedData>(defaultSeedData);
  const [originalSeedData, setOriginalSeedData] = useState<seedData>(defaultSeedData);
  const [selectedSolutionIngredientsRows, setSelectedSolutionIngredientsRows] = useState(
    [],
  );
  const exp = useRMSelector((state) => state.experiment.expData);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getBlankImage = () => {
    return (
      <div id='seedformulationmodal-blankimage' style={styles.blankImageStyle}>
        {isLoading ? 'Loading...' : 'Image Not Available'}
      </div>
    );
  };
  const showFileNotFoundModal = useFileNotFoundModal();

  const seedConditionColumns = useMemo(
    () => [
      {
        Header: <div id='seedformulationmodal-conditioncolums-type'>Type</div>,
        accessor: 'type',
        className: 'w-[50%]',
      },
      {
        Header: <div id='seedformulationmodal-conditioncolums-name'>Name</div>,
        accessor: 'name',
        className: 'w-[50%]',
      },
      {
        Header: <div id='seedformulationmodal-conditioncolums-conc'>Conc.</div>,
        accessor: 'conc',
        className: 'w-[50%]',
      },
      {
        Header: <div id='seedformulationmodal-conditioncolums-concunit'>Conc. Unit</div>,
        accessor: 'concUnit',
        className: 'w-[50%]',
      },
    ],
    [selectedSolutionIngredientsRows],
  );

  useEffect(() => {
    if (isNew) {
      //new seed case
      if (!selectedWells?.length) {
        const id = 'seeduseeffect-alert-noselectedwells';
        document.body.setAttribute('data-active-alert',id);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Please select the desired well(s) to be the source for the new seed.',
        );
        setShow(false);
        return;
      }

      const plateId = selectedWells[0].plateID;
      const otherPlateWells = selectedWells.filter((w) => w.plateID !== plateId);
      if (otherPlateWells.length) {
        const id = 'seeduseeffect-alert-differentplate';
        document.body.setAttribute('data-active-alert',id);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'All source wells must come from the same plate. Please select the desired well(s) from the same plate to create a new seed.',
        );
        setShow(false);
        return;
      }

      if (selectedWells.length > 20) {
        const id = 'seeduseeffect-alert-toomanywells';
        document.body.setAttribute('data-active-alert',id);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Seeds can be created from a maximum of 20 wells. Please select fewer wells to create a new seed.',
        );
        setShow(false);
        return;
      }
    }
      const id = 'seeduseeffect-pageload-start';
      document.body.setAttribute('data-active-step', id);
    PageLoad();
  }, []);

  const setSeedEditState = useCallback(
    (flag) => {
      setIsDirty(flag);
      if (setIsSeedEdited) {
        setIsSeedEdited(flag);
      }
    },
    [setIsSeedEdited],
  );
  const PageLoad = useCallback(async () => {
    let seedDto: SeedDTO;
    setIsLoading(true);
    try {
      if (!isNew) {
        //edit case
        seedDto = await loadSeedDTO(treeNodeId);
        setAccessRightName(seedDto.accessRightName);
        setSeedInfo(seedDto);

        if (seedDto?.latestInspectionId) {
          await getImageList(seedDto);
        }

        if (setSeedName) {
          setSeedName(seedDto.name);
        }
      } else {
        // new case
        seedDto = await createSeedDTO();

        if (seedDto?.latestInspectionId) {
          const imageList = await getImageList(seedDto);

          if (!imageList?.length) {
            const id = 'seedpageload-alert-nowellswithimages';
            document.body.setAttribute('data-active-alert',id);
            showAlertSnackBar(
              EAlertType.ERROR,
              ESnackBarPosition.TOP,
              'Could not find wells with images. Please select desired well(s) having images.',
            );
            setShow(false);
            return;
          }

          seedDto = await saveNewSeed(seedDto);
        } else {
          const id = 'seedpageload-alert-couldnotcreateseed';
          document.body.setAttribute('data-active-alert',id);
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            'Could not create Seed. Please select desired well(s) having images.',
          );
          setShow(false);
          return;
        }
      }
    } catch (error) {
      if (error.status == 404) {
        const id = 'seedpageload-modal-filenotfound';
        document.body.setAttribute('data-active-alert',id);
        showFileNotFoundModal();
      } else {
        const id = 'seedpageload-alert-errorloadingseed';
        showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Error loading Seed.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [treeNodeId, isNew]);

  const createNewSeed = useCallback(async (seedDto: SeedDTO) => {
    const seedformulationClient: SeedFormulationConrollerClient = ModelFactory(
      SeedFormulationConrollerClient,
    );
    const savedSeedDto: SeedDTO = await seedformulationClient.saveSeed(seedDto);

    setSeedInfo(savedSeedDto);
    return savedSeedDto;
  }, []);

  const setSeedInfo = useCallback(
    (seedDto: SeedDTO) => {
      const newSeedData = {
        ...seedDtoData,
        name: seedDto?.name,
        id: seedDto?.id,
        seedDate: seedDto?.dateCreated,
        seedPlateId: seedDto?.plateId,
        seedDropNumber: seedDto?.wellDropNumber,
        seedWellNumbers: seedDto?.wellList,
        pfName: seedDto?.proteinFormulationDTO?.pfName,
        pfIng: seedDto?.proteinFormulationDTO?.proteinFormulationIngredient,
        notesText: seedDto?.notes,
        useWellSolChecked: seedDto?.wellSolutionWellId ? true : false,
        solutionWellId: seedDto?.wellSolutionWellId,
        treeNodeId: seedDto?.treeNodeId,
      };
      setSeedDtoData(newSeedData);
      setOriginalSeedData(newSeedData);

      getSelectedWellItems(seedDto.selectedWells);

      loadSolutionWellIngredientsBySeed(seedDto);
    },
    [seedDtoData],
  );

  const saveNewSeed = useCallback(async (seedDto) => {
    const seedInfo: SeedDTO = await createNewSeed(seedDto);
    return seedInfo;
  }, []);

  const createSeedDTO = useCallback(async () => {
    const seedDto = new SeedDTO();
    seedDto.id = seedDtoData.id;
    if (isNew) {
      //edit seed and save
      seedDto.captureProfileId = cpId;
      seedDto.plateId = plateID;

      const seedWellDtos: SeedWellDTO[] = [];

      selectedWells.forEach((w) => {
        const seedWellDto = new SeedWellDTO();
        const well: WellIng = getWell(w, exp);
        const regionId = getRegionId(
          exp.regions,
          well.wellNumber,
          dropNumber,
          'Drop',
          plateID,
        );
        if (regionId) {
          //Assign anyone drop to seed
          if (!seedDto.wellDropId) {
            const drops: Drop[] = well.dropList.filter((d) => d.dropNumber == dropNumber);
            if (drops.length) {
              const drop = drops[0];
              seedDto.wellDropId = drop.dropId;
              seedDto.wellDropNumber = drop.dropNumber;
              seedDto.proteinFormulationId = drop.proteinFormulationID;
            }
          }

          seedWellDto.wellId = well.wellId;
          seedWellDto.seedID = seedDto.id;
          seedWellDto.wellNumber = well.wellNumber;
          seedWellDto.wellDropRegionId = regionId;
          seedWellDtos.push(seedWellDto);
        }
      });

      seedDto.seedWellDTOs = seedWellDtos;
      seedDto.latestInspectionId = getLatestImageBatchId(exp);

      if (exp.profiles.length > 0) {
        seedDto.captureProfileId = exp.profiles[0].id;
      }
    }

    const seedSolutionIngredientDTOList: SeedSolutionIngredientsDTO[] = [];
    if (seedDtoData.useWellSolChecked) {
      seedDto.wellSolutionWellId = seedDtoData.solutionWellId;

      solutionWellLayerIngListText?.map((ing) => {
        const seedSolutionIngredientDTO: SeedSolutionIngredientsDTO =
          new SeedSolutionIngredientsDTO({
            seedId: seedDto.id,
            type: ing.type,
            name: ing.name,
            concentration: ing.conc,
            concentrationUnit: ing.concUnit,
          });
        seedSolutionIngredientDTOList.push(seedSolutionIngredientDTO);
      });
    } else {
      seedDto.wellSolutionWellId = undefined;
    }

    wellSolutionEditData.map((ing) => {
      if (ing.length) {
        const seedSolutionIngredientDTO: SeedSolutionIngredientsDTO =
          new SeedSolutionIngredientsDTO({
            seedId: seedDto.id,
            type: ing[0],
            name: ing[1],
            concentration: parseInt(ing[2]),
            concentrationUnit: ing[3],
          });
        seedSolutionIngredientDTOList.push(seedSolutionIngredientDTO);
      }
    });

    seedDto.seedSolutionIngredientList = seedSolutionIngredientDTOList;
    seedDto.notes = seedDtoData.notesText;
    seedDto.name = seedDtoData.name;
    seedDto.treeNodeId = seedDtoData.treeNodeId;
    if (seedDtoData.seedPlateId) {
      seedDto.plateId = seedDtoData.seedPlateId;
    }

    return seedDto;
  }, [
    seedDtoData,
    wellNavigationDetails,
    selectedDropImageParameters,
    exp,
    isNew,
    solutionWellLayerIngListText,
    wellSolutionEditData,
  ]); //to be seen later

  const seedValidated = (seedDto: SeedDTO) => {
    if (!seedDto.name || seedDto.name.trim() === '') {
      const id = 'seedpageload-alert-couldnotcreateseed';
      document.body.setAttribute('data-active-alert', id);
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'Seed Name cannot be empty.',
      );
      return false;
    }

    return true;
  };

  const refSaveSeed = useCallback(async () => {
    const seedDto: SeedDTO = await createSeedDTO();
    if (seedValidated(seedDto)) {
      await updateSeed(seedDto, showFileNotFoundModal);
      if (setSeedName) {
        setSeedName(seedDto.name);
      }
      setSeedEditState(false);
      if (setShow) {
        setShow(false);
      }
    }
  }, [createSeedDTO]);

  const discardChanges = useCallback(() => {
    setSeedDtoData(originalSeedData);
    setSeedEditState(false);
  }, [originalSeedData]);

  const refCancelSeed = () => {
    setShow(false);
  };

  const getWell = (wellDetail: WellNavigationDetails, docData: ExperimentDtoClient) => {
    const plates = docData.plateDto.filter((p) => p.plateId == wellDetail.plateID);
    if (plates.length > 0) {
      const currentPlate = plates[0];
      const wells = currentPlate?.ingredients?.wellList?.filter(
        (w) => w.wellNumber == wellDetail.wellNum,
      );
      if (wells?.length > 0) {
        const currentWell = wells[0];
        return currentWell;
      }
    }
  };

  const getSeedPfData = useCallback(() => {
    if (!seedDtoData.pfIng) {
      return;
    }

    const seedPfData = seedDtoData.pfIng.map(
      (pi: PFDocProteinFormulationIngredientsDTO, i) => ({
        srno: i + 1,
        type: getIngListDiv(pi.proteinFormulationIngredientTypeId),
        name: getIngListDiv(pi.name),
        concMg: getIngListDiv(pi.concentrationmgmL),
        concMm: getIngListDiv(pi.concentrationmM),
      }),
    );

    return seedPfData;
  }, [seedDtoData]);

  const getSelectedWellItems = (wells) => {
    if (wells.length == 0) {
      return;
    }

    const selectedWellItems = wells.map((w) => ({
      value: w.id,
      name: w.rowLetter + w.columnNumber,
    }));
    setSelectedWellItems(selectedWellItems);

    loadConditionWellIngredients(selectedWellItems[0].value);
  };

  const onConditionsWellChanged = (e) => {
    const wellId = Number(e.target.value);
    setConditionWellId(wellId);
    loadConditionWellIngredients(wellId);
  };

  const loadConditionWellIngredients = async (wellId) => {
    const wellInfoClient: WellInfoClient = ModelFactory(WellInfoClient);
    const wellLayerIngredientDTO: WellLayerIngredientDTO[] =
      await wellInfoClient.getWellLayerIngredients(wellId);

    const wlIngList =
      wellLayerIngredientDTO &&
      wellLayerIngredientDTO.map((wi: WellLayerIngredientDTO, i) => ({
        type: getIngListDiv(wi.ingredientTypeName),
        name: getIngListDiv(wi.ingredientName),
        conc: getIngListDiv(wi.concentration),
        concUnit: getIngListDiv(wi.concentrationUnit),
      }));

    setWellLayerIngList(wlIngList);
  };

  const onSolutionWellChanged = (e) => {
    const solutionWellId = Number(e.target.value);
    setSeedDtoData({ ...seedDtoData, solutionWellId });
    loadSolutionWellIngredients(solutionWellId);
  };

  const loadSolutionWellIngredients = async (wellId) => {
    const wellInfoClient: WellInfoClient = ModelFactory(WellInfoClient);
    const wellLayerIngredientDTO: WellLayerIngredientDTO[] =
      await wellInfoClient.getWellLayerIngredients(wellId);

    const wlIngListHtml =
      wellLayerIngredientDTO &&
      wellLayerIngredientDTO.map((wi: WellLayerIngredientDTO, i) => ({
        type: getIngListDiv(wi.ingredientTypeName),
        name: getIngListDiv(wi.ingredientName),
        conc: getIngListDiv(wi.concentration),
        concUnit: getIngListDiv(wi.concentrationUnit),
      }));

    const wlIngList =
      wellLayerIngredientDTO &&
      wellLayerIngredientDTO.map((wi: WellLayerIngredientDTO, i) => ({
        type: wi.ingredientTypeName,
        name: wi.ingredientName,
        conc: wi.concentration,
        concUnit: wi.concentrationUnit,
      }));

    setSolutionWellLayerIngList(wlIngListHtml);
    setSolutionWellLayerIngListText(wlIngList);
  };

  const loadSolutionWellIngredientsBySeed = async (seedDto: SeedDTO) => {
    const wlIngListHtml =
      seedDto.seedSolutionIngredientList &&
      seedDto.seedSolutionIngredientList.map((ing: SeedSolutionIngredientsDTO, i) => ({
        type: getIngListDiv(ing.type),
        name: getIngListDiv(ing.name),
        conc: getIngListDiv(ing.concentration),
        concUnit: getIngListDiv(ing.concentrationUnit),
      }));

    const wlIngList =
      seedDto.seedSolutionIngredientList &&
      seedDto.seedSolutionIngredientList.map((ing: SeedSolutionIngredientsDTO, i) => ({
        type: ing.type,
        name: ing.name,
        conc: ing.concentration,
        concUnit: ing.concentrationUnit,
      }));

    setSolutionWellLayerIngListValue(wlIngListHtml);
    setSolutionWellLayerIngListText(wlIngList);
  };

  const onUseWellSolClicked = (e) => {
    const useWellSolChecked = !seedDtoData.useWellSolChecked;
    let solutionWellId = seedDtoData.solutionWellId;

    if (useWellSolChecked) {
      if (!solutionWellId) {
        solutionWellId = selectedWellItems?.[0]?.value;
      }
      loadSolutionWellIngredients(solutionWellId);
    } else {
      setSolutionWellLayerIngList([]);
      solutionWellId = null;
    }

    setSeedEditState(true);
    setSeedDtoData({ ...seedDtoData, useWellSolChecked, solutionWellId });
  };

  const setSolutionWellLayerIngListValue = (ing) => {
    if (ing) {
      setSolutionWellLayerIngList(ing);
    } else {
      setSolutionWellLayerIngList([]);
    }
  };

  const onTypeChanged = (e) => {
    const idStr = String(e.target.id ?? '');
    const numPart = idStr.split('-').pop();
    const index = parseInt(numPart);
    const colName = e.target.name;
    const value = e.target.value;
    const editData = wellSolutionEditData;

    switch (colName) {
      case 'ingType':
        editData[index][0] = value;
        break;
      case 'ingName':
        editData[index][1] = value;
        break;
      case 'ingConc':
        editData[index][2] = value;
        break;
      case 'ingConcUnit':
        editData[index][3] = value;
        break;
      default:
        break;
    }

    setWellSolutionEditData(editData);
    setSeedEditState(true);
  };

  const onAddWellIngredientClicked = (e) => {
    let solWellIng = solutionWellLayerIngList;
    const editData = wellSolutionEditData;
    const index = editData.length;
    if (!editData[index]) {
      editData[index] = [];
    }

    const editIng = {
      type: (
        <input
          id={`editing-type-${index.toString()}`}
          name="ingType"
          className="inputFieldWhiteBackground"
          style={{ width: '3.5rem' }}
          onChange={onTypeChanged}
        />
      ),
      name: (
        <input
          id={`editing-name-${index.toString()}`}
          name="ingName"
          className="inputFieldWhiteBackground"
          style={{ width: '5rem' }}
          onChange={onTypeChanged}
        />
      ),
      conc: (
        <input
          id={`editing-conc-${index.toString()}`}
          name="ingConc"
          className="inputFieldWhiteBackground"
          style={{ width: '3.5rem' }}
          onChange={onTypeChanged}
        />
      ),
      concUnit: (
        <input
          id={`editing-conunit-${index.toString()}`}
          name="ingConcUnit"
          className="inputFieldWhiteBackground"
          style={{ width: '3.5rem' }}
          onChange={onTypeChanged}
        />
      ),
    };

    solWellIng = solWellIng.concat([editIng]);

    setSolutionWellLayerIngList(solWellIng);

    setSeedEditState(true);
  };

  const onRemoveWellIngredientClicked = (e) => {
    const solWellIngHtml = solutionWellLayerIngList.removeByIndexes(
      selectedSolutionIngredientsRows,
    );

    const solWellIng = solutionWellLayerIngListText.removeByIndexes(
      selectedSolutionIngredientsRows,
    );

    setSolutionWellLayerIngList(solWellIngHtml);
    setSolutionWellLayerIngListText(solWellIng);

    const editStartIndex =
      solutionWellLayerIngList.length -
      (solutionWellLayerIngList.length - solutionWellLayerIngListText.length);
    const editIndexesToRemove = selectedSolutionIngredientsRows
      .filter((item) => item >= editStartIndex)
      .map((item) => item - solutionWellLayerIngListText.length + 1);

    const editData = wellSolutionEditData.removeByIndexes(editIndexesToRemove);
    setWellSolutionEditData(editData);

    setSeedEditState(true);
    setSelectedSolutionIngredientsRows([]);
  };

  const onNotesChanged = (e) => {
    setSeedDtoData({ ...seedDtoData, notesText: e.target.value });
    setSeedEditState(true);
  };

  const getImageList = async (seedDto: SeedDTO) => {
    const wellService = new WellService();
    const wellImageList = [];

    const batchId = seedDto.latestInspectionId;

    for (const seedWellDto of seedDto.seedWellDTOs) {
      const regionId = seedWellDto.wellDropRegionId;

      if (!regionId) {
        continue;
      }

      const wellImage = await wellService.getSeedWellImage(
        seedDto.plateId,
        seedWellDto.wellNumber,
        batchId,
        seedDto.wellDropNumber,
        seedDto.captureProfileId,
        'Drop',
        regionId,
        false,
      );
      if (wellImage.wellImage) {
        wellImageList.push(wellImage.wellImage);
      }
    }

    setSelectedImages(wellImageList);

    return wellImageList;
  };

  const getFormatedDate = (date) => {
    if (!date) {
      return;
    }
    const inputDate = new Date(date);
    const dateString = inputDate.toLocaleDateString('en-US');
    return dateString;
  };

  const onSeedNameChanged = (e) => {
    setSeedDtoData({ ...seedDtoData, name: e.target.value });
    setSeedEditState(true);
  };

  useImperativeHandle(
    ref,
    () => ({
      refSaveSeed,
      discardChanges,
    }),
    [refSaveSeed, discardChanges],
  );

  const handleSelectionChange = useCallback((rowIndices) => {
    setSelectedSolutionIngredientsRows([...rowIndices]);
  }, []);

  return (
    <div id="seedformulationmodal-main-container" style={{ fontSize: '12px' }}>
      <FmlxBackdropLoading id="seedformulationmodal-backdroploading" description="Loading..." show={isLoading} />
      <Row  id="seedformulationmodal-row">
        <Col id="seedformulationmodal-col-seedsource" style={{ width: '30%' }}>
          <div id="seedformulationmodal-seedsource-wrapper" className="flex flex-x-center idr-wrapper-content margin5 seedSourceSize">
            <div id="seedformulationmodal-seedsource-heading" className="headingStyle">Seed Source</div>
            <div id="seedformulationmodal-seedsource-content" className="ml-2">
              <div id="seedformulationmodal-seedsource-name-container">
                <label id="seedformulationmodal-seedsource-name-label"  style={styles.labelStyle}>Seed Name</label>
                <input
                  id="seedformulationmodal-seedsource-name-input"
                  style={styles.inputStyle}
                  value={seedDtoData.name}
                  type="text"
                  onChange={onSeedNameChanged}
                  readOnly={isReadOnly}
                />
              </div>
              <LabelTextBox
                labelText="Date"
                inputStyle={styles.inputStyle}
                labelStyle={styles.labelStyle}
                inputValue={getFormatedDate(seedDtoData.seedDate)}
                disabled={true}
              ></LabelTextBox>
              <div id="seedformulationmodal-seedsource-plate-drop-container"  className="flex" style={{ width: '343px' }}>
                <LabelTextBox
                  labelText="Plate ID"
                  inputStyle={styles.inputStylePlateId}
                  labelStyle={styles.labelStylePlateId}
                  inputValue={seedDtoData.seedPlateId}
                  disabled={true}
                ></LabelTextBox>
                <LabelTextBox
                  labelText="Drop"
                  inputStyle={styles.inputStyleDrop}
                  labelStyle={styles.labelStyleDrop}
                  inputValue={seedDtoData.seedDropNumber}
                  disabled={true}
                ></LabelTextBox>
              </div>
              <LabelTextBox
                labelText="Well"
                inputStyle={styles.inputStyle}
                labelStyle={styles.labelStyle}
                inputValue={seedDtoData.seedWellNumbers}
                disabled={true}
              ></LabelTextBox>
            </div>
          </div>
          <div id="seedformulationmodal-dropimage-wrapper"  className="flex flex-x-center idr-wrapper-content margin5 dropImageSize h-70">
            <div id="seedformulationmodal-dropimage-heading" className="headingStyle">Drop Image</div>
            {selectedImages?.length ? (
              <RecentImagedCarousel  ImageList={selectedImages} />
            ) : (
              <div id="seedformulationmodal-dropimage-blank">{getBlankImage()}</div>
            )}
          </div>
        </Col>
        <Col id="seedformulationmodal-col-proteinformulation" style={{ width: '30%' }}>
          <div id="seedformulationmodal-proteinformulation-wrapper" className="flex idr-wrapper-content margin5 proteinFormulationSize">
            <div id="seedformulationmodal-proteinformulation-heading" className="headingStyle">Protein Formulation</div>
            <LabelTextBox
              labelText="Protein Name"
              inputStyle={styles.inputStyle}
              labelStyle={styles.labelStyle}
              inputValue={seedDtoData.pfName}
              disabled={true}
            ></LabelTextBox>

            <div id="seedformulationmodal-proteinformulation-tablecontainer" className="proteinFormulationDataSize">
              <RMTable
                columns={seedPfColumns}
                tableClass="w-full"
                data={getSeedPfData() ?? []}
              />
            </div>
          </div>

          <div id="seedformulationmodal-conditions-wrapper" className="flex flex-space-between idr-wrapper-content margin5 conditionSize">
            <div id="seedformulationmodal-conditions-heading" className="headingStyle">Conditions</div>
            <div id="seedformulationmodal-conditions-selectbox-container">
              <EISelectBox
                labelText="Well Number"
                selectedValue={conditionWellId}
                options={selectedWellItems}
                onChange={onConditionsWellChanged}
                labelStyle={styles.labelStyle}
                selectStyle={styles.inputStyle}
                disabled={!selectedWellItems.length || isReadOnly}
              ></EISelectBox>
            </div>

            <div id="seedformulationmodal-conditions-tablecontainer" className="conditionTableSize">
              <RMTable
                columns={seedConditionColumns}
                tableClass="w-full"
                data={wellLayerIngList}
              />
            </div>
          </div>
        </Col>
        <Col id="seedformulationmodal-col-stabilizingsolution" style={{ width: '30%' }}>
          <div id="seedformulationmodal-stabilizingsolution-wrapper" className="flex flex-space-between idr-wrapper-content margin5">
            <div id="seedformulationmodal-stabilizingsolution-heading "className="headingStyle">Stabilizing Solution Ingredients</div>
            <div id="seedformulationmodal-stabilizingsolution-fieldssetcontainer" className="flex flex-space-between items-center margin5">
              <fieldset
                id="seedformulationmodal-stabilizingsolution-fieldset"
                className="flex flex-row items-center"
                disabled={!selectedWellItems.length}
              >
                <CustomCheckBox
                  id="seedformulationmodal-stabilizingsolution-wellSolCheck"
                  onChange={onUseWellSolClicked}
                  label=""
                  checked={seedDtoData.useWellSolChecked}
                  disabled={isReadOnly}
                />
                <EISelectBox
                  labelText="Use Well Solution"
                  selectedValue={seedDtoData.solutionWellId}
                  options={selectedWellItems}
                  onChange={onSolutionWellChanged}
                  labelStyle={styles.solutionBoxLabelStyle}
                  selectStyle={styles.solutionBoxSelectStyle}
                  disabled={isReadOnly}
                ></EISelectBox>
              </fieldset>
            </div>

            <div id="seedformulationmodal-stabilizingsolution-tablecontainer" className="solutionTableStyle">
              {solutionWellLayerIngList && (
                <RMTable
                  tableClass="w-full"
                  columns={seedConditionColumns}
                  selectedIds={selectedSolutionIngredientsRows}
                  onCheckboxSelectionChange={handleSelectionChange}
                  data={solutionWellLayerIngList}
                />
              )}
            </div>
            <div id="seedformulationmodal-stabilizingsolution-buttoncontainer" className="ingredientButtonContainer">
              <RMCustomButton
                iconProps={{ name: 'SeedAddIng', width: '44px', height: '44px' }}
                contentContainerStyle={styles.addButtonStyle}
                onClick={onAddWellIngredientClicked}
                isDisabled={isReadOnly}
              />
              <RMCustomButton
                iconProps={{
                  name: 'Delete',
                  width: '24px',
                  height: '24px',
                  fill: 'white',
                }}
                contentContainerStyle={styles.removeButtonStyle}
                isDisabled={!selectedSolutionIngredientsRows?.length || isReadOnly}
                onClick={onRemoveWellIngredientClicked}
              />
            </div>
          </div>
          <div  id="seedformulationmodal-notes-wrapper"  className="flex flex-space-between idr-wrapper-content margin5 max-h-[14vh]">
            <div id="seedformulationmodal-notes-heading" className="headingStyle">Notes</div>
            <div id="seedformulationmodal-notes-inputcontainer">
              <input
                id="seedformulationmodal-notes-input"
                className="notesStyle"
                onChange={onNotesChanged}
                value={seedDtoData.notesText}
                readOnly={isReadOnly}
              ></input>
            </div>
          </div>
        </Col>
      </Row>
      {isNew && (
        <Row  id="seedformulationmodal-buttonrow" className="buttonRowStyle mb-2">
          <Col id="seedformulationmodal-buttonrow-left" className="buttonRowStyle" />
          <Col  id="seedformulationmodal-buttonrow-middle" className="buttonRowStyle"></Col>
          <Col  id="seedformulationmodal-buttonrow-right" className="buttonRowStyle">
            <div id="seedformulationmodal-buttoncontainer" className="flex flex-space-between buttonContainerStyle mt-3">
              <RMCustomButton
                contentContainerClass="btn-outline-yellow-wide"
                onClick={refSaveSeed} //TODO: this is temp.this needs to be fixed.
                title="Save"
                isDisabled={!isDirty}
              />
              <RMCustomButton
                contentContainerClass="btn-outline-yellow-wide mr-3"
                onClick={refCancelSeed}
                title="Cancel"
              />
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
});
